import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Building2, Users, Truck, X,
  CheckCircle2, Loader2, TrendingUp,
  ChevronLeft, ChevronRight, Trophy,
  DollarSign, FileText, CreditCard,
  Search, Settings, Unlock,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import CarrierBilling from "@/pages/market/CarrierBilling";
import { CarrierOwnProfile, CarrierDirectory } from "./CarrierProfile";
import { MyDriversPanel } from "./MyDriversPanel";
import { DriverCard } from "./DriverCard";
import {
  ACCENT, GOLD, MID, STAGGER_CONTAINER,
} from "./constants";
import { timeAgo, LiveDot } from "./MarketUI";

const BOARD_PAGE_SIZE = 12;

export function CarrierHub({
  marketUser,
  leaderboard,
  bids,
  drivers,
  token,
  openBidModal,
  setShowAgreement,
  docCredits,
  setCarrierDocUploadBid,
  annulmentHistory,
  setPaymentNotif,
  setShowPaymentModal,
  transferAlerts,
  dataLoading,
  setupComplete,
  handleHubClick,
  apiBase,
  setSelectedDriver,
}) {
  const [carrierTab, setCarrierTab]       = useState("board");
  const [boardSearch, setBoardSearch]     = useState("");
  const [boardPage, setBoardPage]         = useState(1);
  const [boardFreight, setBoardFreight]   = useState("all");
  const [tenstreetId, setTenstreetId]     = useState("");
  const [tenstreetSaving, setTenstreetSaving] = useState(false);

  // Reset page when search or freight filter changes
  useEffect(() => { setBoardPage(1); }, [boardSearch, boardFreight]);

  return (
    <section id="carrier-hub-section" style={{ scrollMarginTop: 80 }}>
      {/* ── Carrier welcome banner + tab switcher ── */}
      {marketUser?.hub === "carrier" && marketUser.carrier_name && (
        <div
          className="mb-6 px-5 py-4 flex items-center flex-wrap gap-4"
          style={{ background: "rgba(255,77,0,0.07)", border: "1px solid rgba(255,77,0,0.2)" }}
        >
          <Building2 className="w-5 h-5 shrink-0" style={{ color: ACCENT }} />
          <div className="flex-1 min-w-0">
            <p className="font-head text-[10px] uppercase tracking-[0.3em] text-white/40 mb-0.5">Logged in as</p>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-head text-base font-black uppercase tracking-[0.06em] text-white">{marketUser.carrier_name}</p>
              {leaderboard[0]?.carrier_name?.toLowerCase() === marketUser.carrier_name?.toLowerCase() && (
                <span
                  data-testid="carrier-leaderboard-crown"
                  className="flex items-center gap-1 px-2 py-0.5 font-head text-[9px] uppercase tracking-[0.15em]"
                  style={{ background: "rgba(245,200,66,0.12)", border: "1px solid rgba(245,200,66,0.35)", color: GOLD }}
                  title="You are the #1 carrier on the Hylas Market leaderboard!"
                >
                  <Trophy className="w-3 h-3" />
                  #1 Market Leader
                </span>
              )}
              {marketUser.carrier_agreement_signed ? (
                <span className="flex items-center gap-1 font-head text-[9px] uppercase tracking-[0.12em] px-2 py-0.5"
                  style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)", color: "#22C55E" }}>
                  <CheckCircle2 className="w-2.5 h-2.5" />Agreement Signed
                </span>
              ) : (
                <button
                  onClick={() => setShowAgreement(true)}
                  className="flex items-center gap-1 font-head text-[9px] uppercase tracking-[0.12em] px-2 py-0.5 transition-colors hover:border-orange-400/50"
                  style={{ background: "rgba(255,77,0,0.07)", border: "1px solid rgba(255,77,0,0.25)", color: "rgba(255,77,0,0.7)" }}
                >
                  <FileText className="w-2.5 h-2.5" />Sign Agreement
                </button>
              )}
            </div>
          </div>
          {/* Cashback wallet balance */}
          {marketUser.cashback_balance > 0 && (
            <div
              className="flex items-center gap-2 px-3 py-2"
              style={{ background: "rgba(56,189,248,0.07)", border: "1px solid rgba(56,189,248,0.2)" }}
              data-testid="carrier-cashback-badge"
              title="Earned by your Safety team uploading MVR/PSP/Clearinghouse docs. Apply to next placement fee."
            >
              <DollarSign className="w-3.5 h-3.5 shrink-0" style={{ color: "#38BDF8" }} />
              <div>
                <p className="font-head text-[9px] uppercase tracking-[0.15em] text-white/40">Cashback Wallet</p>
                <p className="font-head text-sm font-black" style={{ color: "#38BDF8" }}>${marketUser.cashback_balance}</p>
              </div>
            </div>
          )}
          {/* Tenstreet Company ID setup */}
          <div
            data-testid="carrier-tenstreet-setup"
            className="flex items-center gap-2 px-3 py-2"
            style={{ background: "rgba(147,51,234,0.07)", border: "1px solid rgba(147,51,234,0.2)" }}
            title="Set your Tenstreet Company ID so Hylas auto-pushes driver applications to your dashboard on transfer"
          >
            <Settings className="w-3.5 h-3.5 shrink-0" style={{ color: "#9333EA" }} />
            <div>
              <p className="font-head text-[9px] uppercase tracking-[0.15em] text-white/40">Tenstreet ID</p>
              <div className="flex items-center gap-1.5">
                <input
                  data-testid="carrier-tenstreet-id-input"
                  type="text"
                  value={tenstreetId}
                  onChange={(e) => setTenstreetId(e.target.value)}
                  placeholder="Company ID…"
                  className="w-24 bg-transparent text-xs text-white/80 placeholder-white/25 outline-none border-b border-white/15 py-0.5 focus:border-purple-400/50 transition-colors"
                />
                <button
                  data-testid="carrier-tenstreet-save-btn"
                  disabled={tenstreetSaving || !tenstreetId.trim()}
                  onClick={async () => {
                    const tok = localStorage.getItem("hylas_market_token");
                    setTenstreetSaving(true);
                    try {
                      await api.marketUpdateTenstreetId(tok, tenstreetId.trim());
                      toast.success("Tenstreet Company ID saved — auto-push enabled");
                    } catch {
                      toast.error("Failed to save Tenstreet ID");
                    } finally {
                      setTenstreetSaving(false);
                    }
                  }}
                  className="font-head text-[8px] uppercase tracking-[0.1em] px-1.5 py-0.5 transition-all hover:brightness-110 disabled:opacity-40"
                  style={{ background: "rgba(147,51,234,0.2)", color: "#9333EA", border: "1px solid rgba(147,51,234,0.3)" }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
          {/* Tab switcher */}
          <div className="flex gap-2 ml-auto flex-wrap">
            <button
              data-testid="carrier-tab-board"
              onClick={() => setCarrierTab("board")}
              className="flex items-center gap-1.5 px-4 py-2 font-head text-[10px] uppercase tracking-[0.15em] transition-all"
              style={{
                background: carrierTab === "board" ? "rgba(255,77,0,0.15)" : "transparent",
                border: `1px solid ${carrierTab === "board" ? "rgba(255,77,0,0.5)" : "rgba(255,255,255,0.12)"}`,
                color: carrierTab === "board" ? ACCENT : "rgba(255,255,255,0.45)",
              }}
            >
              <Truck className="w-3.5 h-3.5" />
              Transfer Board
            </button>
            <button
              data-testid="carrier-tab-mybids"
              onClick={() => setCarrierTab("mybids")}
              className="relative flex items-center gap-1.5 px-4 py-2 font-head text-[10px] uppercase tracking-[0.15em] transition-all"
              style={{
                background: carrierTab === "mybids" ? "rgba(255,77,0,0.15)" : "transparent",
                border: `1px solid ${carrierTab === "mybids" ? "rgba(255,77,0,0.5)" : "rgba(255,255,255,0.12)"}`,
                color: carrierTab === "mybids" ? ACCENT : "rgba(255,255,255,0.45)",
              }}
            >
              <DollarSign className="w-3.5 h-3.5" />
              My Bids ({bids.filter((b) => (b.carrier_name || "").toLowerCase() === (marketUser.carrier_name || "").toLowerCase()).length})
              {transferAlerts.length > 0 && (
                <span data-testid="carrier-transfer-alert-badge"
                  className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center rounded-full font-head text-[9px] font-black text-black"
                  style={{ background: "#ef4444", border: "1px solid #b91c1c" }}>
                  {transferAlerts.length}
                </span>
              )}
            </button>
            <button
              data-testid="carrier-tab-billing"
              onClick={() => setCarrierTab("billing")}
              className="flex items-center gap-1.5 px-4 py-2 font-head text-[10px] uppercase tracking-[0.15em] transition-all"
              style={{
                background: carrierTab === "billing" ? "rgba(255,77,0,0.15)" : "transparent",
                border: `1px solid ${carrierTab === "billing" ? "rgba(255,77,0,0.5)" : "rgba(255,255,255,0.12)"}`,
                color: carrierTab === "billing" ? ACCENT : "rgba(255,255,255,0.45)",
              }}
            >
              <CreditCard className="w-3.5 h-3.5" />
              Billing
            </button>
            <button
              data-testid="carrier-tab-profile"
              onClick={() => setCarrierTab("profile")}
              className="flex items-center gap-1.5 px-4 py-2 font-head text-[10px] uppercase tracking-[0.15em] transition-all"
              style={{
                background: carrierTab === "profile" ? "rgba(255,77,0,0.15)" : "transparent",
                border: `1px solid ${carrierTab === "profile" ? "rgba(255,77,0,0.5)" : "rgba(255,255,255,0.12)"}`,
                color: carrierTab === "profile" ? ACCENT : "rgba(255,255,255,0.45)",
              }}
            >
              <Building2 className="w-3.5 h-3.5" />
              My Profile
            </button>
            <button
              data-testid="carrier-tab-mydrivers"
              onClick={() => setCarrierTab("mydrivers")}
              className="flex items-center gap-1.5 px-4 py-2 font-head text-[10px] uppercase tracking-[0.15em] transition-all"
              style={{
                background: carrierTab === "mydrivers" ? "rgba(34,197,94,0.15)" : "transparent",
                border: `1px solid ${carrierTab === "mydrivers" ? "rgba(34,197,94,0.5)" : "rgba(255,255,255,0.12)"}`,
                color: carrierTab === "mydrivers" ? "#22C55E" : "rgba(255,255,255,0.45)",
              }}
            >
              <Unlock className="w-3.5 h-3.5" />
              My Drivers
            </button>
            <button
              data-testid="carrier-tab-network"
              onClick={() => setCarrierTab("network")}
              className="flex items-center gap-1.5 px-4 py-2 font-head text-[10px] uppercase tracking-[0.15em] transition-all"
              style={{
                background: carrierTab === "network" ? "rgba(255,77,0,0.15)" : "transparent",
                border: `1px solid ${carrierTab === "network" ? "rgba(255,77,0,0.5)" : "rgba(255,255,255,0.12)"}`,
                color: carrierTab === "network" ? ACCENT : "rgba(255,255,255,0.45)",
              }}
            >
              <Users className="w-3.5 h-3.5" />
              Carriers
            </button>
            <button
              data-testid="carrier-tab-credits"
              onClick={() => setCarrierTab("credits")}
              className="relative flex items-center gap-1.5 px-4 py-2 font-head text-[10px] uppercase tracking-[0.15em] transition-all"
              style={{
                background: carrierTab === "credits" ? "rgba(245,200,66,0.15)" : "transparent",
                border: `1px solid ${carrierTab === "credits" ? "rgba(245,200,66,0.5)" : "rgba(255,255,255,0.12)"}`,
                color: carrierTab === "credits" ? "#F5C842" : "rgba(255,255,255,0.45)",
              }}
            >
              <DollarSign className="w-3.5 h-3.5" />
              Credits
              {docCredits?.available_total > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full font-black text-[8px]"
                  style={{ background: "#F5C842", color: "#000" }}>
                  ${docCredits.available_total}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── BILLING tab ── */}
      {marketUser?.hub === "carrier" && carrierTab === "billing" && (
        <div>
          <div className="mb-6">
            <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Carrier Hub</p>
            <h2 className="font-head text-2xl font-black uppercase tracking-[0.04em]" style={{ color: ACCENT }}>
              Billing & Plans
            </h2>
            <p className="text-xs text-white/30 mt-1">
              Manage your Hylas Market subscription, placement fees and Weekly Flex billing.
            </p>
          </div>
          <CarrierBilling marketUser={marketUser} />
        </div>
      )}

      {/* ── PROFILE tab ── */}
      {marketUser?.hub === "carrier" && carrierTab === "profile" && (
        <div className="-mx-4 sm:-mx-6">
          <CarrierOwnProfile
            token={token}
            bids={bids}
            carrierName={marketUser?.carrier_name || ""}
          />
          {annulmentHistory.length > 0 && (
            <div className="px-4 sm:px-6 pb-8 mt-8">
              <div className="mb-4">
                <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Carrier Hub</p>
                <h2 className="font-head text-xl font-black uppercase tracking-[0.04em]" style={{ color: "#ef4444" }}>
                  Annulment Notices
                </h2>
                <p className="text-xs text-white/30 mt-1">Full history of placement agreements cancelled by Hylas Companion.</p>
              </div>
              <div className="space-y-2">
                {annulmentHistory.map(n => (
                  <div key={n.notification_id} className="px-4 py-3"
                    style={{ background: "rgba(239,68,68,0.04)", border: "1px solid rgba(239,68,68,0.15)" }}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-head text-xs font-bold text-white">{n.driver_name || "—"}</p>
                        <span className="inline-block mt-1 px-2 py-0.5 font-head text-[9px] uppercase tracking-[0.1em]"
                          style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.25)" }}>
                          {n.annulment_reason}
                        </span>
                        {n.annulment_notes && (
                          <p className="text-[10px] mt-1.5 leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>{n.annulment_notes}</p>
                        )}
                      </div>
                      <p className="text-[9px] shrink-0" style={{ color: "rgba(255,255,255,0.25)" }}>
                        {n.created_at ? new Date(n.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── CREDITS tab ── */}
      {marketUser?.hub === "carrier" && carrierTab === "credits" && (
        <div>
          <div className="mb-6">
            <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Carrier Hub</p>
            <h2 className="font-head text-2xl font-black uppercase tracking-[0.04em]" style={{ color: "#F5C842" }}>
              Doc Upload Credits
            </h2>
            <p className="text-xs text-white/30 mt-1">
              Upload MVR, PSP, or Clearinghouse for a driver you bid on. Earn $30 credit per driver if you don't end up hiring them.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            {[
              { label: "Available Credit", value: `$${docCredits?.available_total || 0}`, color: "#22C55E" },
              { label: "Pending Credits", value: docCredits?.pending_count || 0, sub: "awaiting outcome", color: "#F5C842" },
              { label: "Credit = $30/driver", value: "MVR + PSP + CLR", sub: "all three = 1 credit", color: ACCENT },
            ].map(({ label, value, sub, color }) => (
              <div key={label} className="px-4 py-4"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
                <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-1">{label}</p>
                <p className="font-head font-black text-2xl" style={{ color }}>{value}</p>
                {sub && <p className="text-[10px] text-white/25 mt-0.5">{sub}</p>}
              </div>
            ))}
          </div>

          <div className="px-4 py-3 mb-6 flex items-start gap-3"
            style={{ background: "rgba(245,200,66,0.04)", border: "1px solid rgba(245,200,66,0.15)" }}>
            <DollarSign className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "#F5C842" }} />
            <p className="text-xs text-white/50 leading-relaxed">
              <span className="font-bold text-white/70">How credits work:</span> Upload any or all of MVR, PSP, Clearinghouse for a driver you bid on → earns one $30 pending credit. Credit unlocks automatically if that driver gets placed with a different carrier. Credits are voided if you win the driver. Use available credits when placing bids to reduce your placement fee.
            </p>
          </div>

          {docCredits?.credits?.length > 0 ? (
            <div>
              <p className="font-head text-[9px] uppercase tracking-[0.25em] text-white/25 mb-3">Credit History</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                      {["Driver", "Triggered By", "Status", "Amount", "Date"].map(h => (
                        <th key={h} className="font-head text-[9px] uppercase tracking-[0.2em] text-white/25 text-left px-3 py-2">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {docCredits.credits.map((c, i) => {
                      const statusColor = c.status === "available" ? "#22C55E" : c.status === "void" ? "#ef4444" : "#F5C842";
                      return (
                        <tr key={c.credit_id || i} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                          <td className="px-3 py-2.5 font-head font-bold text-white">{c.driver_name || "—"}</td>
                          <td className="px-3 py-2.5 text-white/40 capitalize">{(c.triggered_by || "—").replace("_", " ")}</td>
                          <td className="px-3 py-2.5">
                            <span className="font-head text-[9px] font-black uppercase px-2 py-0.5"
                              style={{ background: `${statusColor}18`, color: statusColor, border: `1px solid ${statusColor}44` }}>
                              {c.status}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 font-head font-black" style={{ color: "#F5C842" }}>+${c.amount}</td>
                          <td className="px-3 py-2.5 text-white/30">
                            {c.created_at ? new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="py-12 text-center text-white/20 text-sm font-head uppercase tracking-[0.2em]">
              No credits yet — upload docs for a driver you've bid on to earn your first $30
            </div>
          )}
        </div>
      )}

      {/* ── MY DRIVERS tab ── */}
      {marketUser?.hub === "carrier" && carrierTab === "mydrivers" && (
        <MyDriversPanel token={token} hub="carrier" />
      )}

      {/* ── NETWORK tab ── */}
      {marketUser?.hub === "carrier" && carrierTab === "network" && (
        <div>
          <div className="mb-6">
            <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Carrier Hub</p>
            <h2 className="font-head text-2xl font-black uppercase tracking-[0.04em]" style={{ color: ACCENT }}>
              Carrier Network
            </h2>
            <p className="text-xs text-white/30 mt-1">
              Browse other carriers active on the Hylas platform.
            </p>
          </div>
          <CarrierDirectory token={token} selfCarrierName={marketUser?.carrier_name} />
        </div>
      )}

      {/* ── MY BIDS tab ── */}
      {marketUser?.hub === "carrier" && carrierTab === "mybids" ? (
        <div>
          <div className="mb-4">
            <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Carrier Hub</p>
            <h2 className="font-head text-2xl font-black uppercase tracking-[0.04em]" style={{ color: ACCENT }}>
              My Bids
            </h2>
          </div>
          {(() => {
            const myBids = bids.filter((b) => (b.carrier_name || "").toLowerCase() === (marketUser.carrier_name || "").toLowerCase());
            const activeBids    = myBids.filter((b) => b.status === "live").length;
            const wins          = myBids.filter((b) => b.status === "transferred").length;
            const totalSpend    = myBids.reduce((s, b) => s + (b.placement_fee || 0), 0);
            const counterOffers = myBids.filter((b) => b.counter_offer_fee).length;

            const MetricCard = ({ label, value, sub, accent }) => (
              <div className="flex-1 min-w-[110px] px-5 py-4"
                style={{ background: MID, border: "1px solid rgba(255,255,255,0.07)" }}>
                <p className="font-head text-[9px] uppercase tracking-[0.25em] text-white/30 mb-2">{label}</p>
                <p className="font-head text-2xl font-black tracking-tight" style={{ color: accent || "white" }}>{value}</p>
                {sub && <p className="font-head text-[10px] text-white/30 mt-1 uppercase tracking-[0.15em]">{sub}</p>}
              </div>
            );

            return (
              <>
                <div className="flex flex-wrap gap-3 mb-5">
                  <MetricCard label="Total Bids" value={myBids.length} sub={myBids.length === 1 ? "bid placed" : "bids placed"} />
                  <MetricCard label="Active Bids" value={activeBids} accent={activeBids > 0 ? ACCENT : undefined} sub="live" />
                  <MetricCard label="Wins" value={wins} accent={wins > 0 ? GOLD : undefined} sub={wins === 1 ? "transfer" : "transfers"} />
                  <MetricCard label="Total Spend" value={totalSpend > 0 ? `$${totalSpend.toLocaleString()}` : "$0"} sub="placement fees" />
                  {counterOffers > 0 && (
                    <div className="flex-1 min-w-[110px] px-5 py-4 animate-pulse"
                      style={{ background: "rgba(245,200,66,0.07)", border: "1px solid rgba(245,200,66,0.35)" }}>
                      <p className="font-head text-[9px] uppercase tracking-[0.25em] mb-2" style={{ color: "rgba(245,200,66,0.6)" }}>Counter-Offers</p>
                      <p className="font-head text-2xl font-black tracking-tight" style={{ color: GOLD }}>{counterOffers}</p>
                      <p className="font-head text-[10px] text-white/30 mt-1 uppercase tracking-[0.15em]">need review</p>
                    </div>
                  )}
                </div>

                {myBids.length === 0 ? (
                  <div data-testid="carrier-mybids-empty"
                    className="flex flex-col items-center py-16 text-center"
                    style={{ background: MID, border: "1px solid rgba(255,77,0,0.1)" }}>
                    <DollarSign className="w-10 h-10 mb-3 opacity-20" style={{ color: ACCENT }} />
                    <p className="font-head text-sm text-white/30 uppercase tracking-[0.2em] mb-1">No bids placed yet</p>
                    <p className="text-xs text-white/20 max-w-xs">Switch to Transfer Board to browse available drivers and place your first bid.</p>
                    <button onClick={() => setCarrierTab("board")}
                      className="mt-5 px-5 py-2 font-head text-[10px] uppercase tracking-[0.2em] transition-all hover:brightness-110"
                      style={{ background: ACCENT, color: "white" }}>
                      Browse Drivers →
                    </button>
                  </div>
                ) : (
                  <div data-testid="carrier-mybids-list"
                    style={{ background: MID, border: "1px solid rgba(255,255,255,0.07)" }}>
                    <div className="px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                      <p className="font-head text-[10px] uppercase tracking-[0.2em] text-white/40">
                        {myBids.length} bid{myBids.length !== 1 ? "s" : ""} placed
                      </p>
                      {transferAlerts.length > 0 && (
                        <div data-testid="carrier-transfer-alert-banner"
                          className="mt-2 flex items-start gap-2 px-3 py-2.5"
                          style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)" }}>
                          <span className="shrink-0 w-4 h-4 flex items-center justify-center rounded-full text-[9px] font-black text-white mt-0.5"
                            style={{ background: "#ef4444" }}>{transferAlerts.length}</span>
                          <div>
                            <p className="font-head text-[10px] font-bold uppercase tracking-[0.1em] text-white">
                              {transferAlerts.length === 1 ? "1 driver was transferred" : `${transferAlerts.length} drivers were transferred`} to another carrier
                            </p>
                            <p className="text-[10px] mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>
                              {transferAlerts.map((a) => a.driver_name).join(", ")}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="divide-y divide-white/05 max-h-[600px] overflow-y-auto">
                      {myBids.map((b) => {
                        const bidStatusColors = {
                          live:             { color: ACCENT,    bg: "rgba(255,77,0,0.1)",    border: "rgba(255,77,0,0.3)",    label: "Live" },
                          payment_pending:  { color: GOLD,      bg: "rgba(245,200,66,0.12)", border: "rgba(245,200,66,0.4)",   label: "Payment Pending" },
                          receipt_uploaded: { color: "#3B82F6", bg: "rgba(59,130,246,0.1)",  border: "rgba(59,130,246,0.35)", label: "Receipt Sent" },
                          transferred:      { color: "#22C55E", bg: "rgba(34,197,94,0.1)",   border: "rgba(34,197,94,0.3)",   label: "Unlocked" },
                          declined:         { color: "#ef4444", bg: "rgba(239,68,68,0.1)",   border: "rgba(239,68,68,0.3)",   label: "Declined" },
                          expired:          { color: "#555",    bg: "rgba(255,255,255,0.04)", border: "rgba(255,255,255,0.1)", label: "Expired" },
                        };
                        const sc    = bidStatusColors[b.status] || bidStatusColors.live;
                        const alert = transferAlerts.find((a) => a.driver_id === b.driver_id);
                        const hasCounter = !!b.counter_offer_fee;
                        return (
                          <div key={b.id} data-testid={`carrier-bid-row-${b.id}`}
                            className="px-4 py-4 flex flex-wrap items-start gap-4"
                            style={{ background: hasCounter ? "rgba(245,200,66,0.03)" : alert ? "rgba(239,68,68,0.04)" : "transparent" }}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <p className="font-head text-sm font-bold text-white">{b.driver_name}</p>
                                <span className="font-head text-[9px] font-black uppercase px-2 py-0.5"
                                  style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
                                  {sc.label}
                                </span>
                                {alert && (
                                  <span data-testid={`carrier-bid-transferred-tag-${b.id}`}
                                    className="font-head text-[9px] font-black uppercase px-2 py-0.5"
                                    style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.35)" }}>
                                    Taken → {alert.transferred_to}
                                  </span>
                                )}
                                {hasCounter && (
                                  <span data-testid={`carrier-counter-offer-badge-${b.id}`}
                                    className="font-head text-[9px] font-black uppercase px-2 py-0.5 animate-pulse"
                                    style={{ background: "rgba(245,200,66,0.15)", color: GOLD, border: "1px solid rgba(245,200,66,0.45)" }}>
                                    Admin suggests ${b.counter_offer_fee?.toLocaleString()}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-white/50 leading-relaxed">
                                {b.position_type?.replace("_", " ")} · {b.equipment_package} ·{" "}
                                {b.pay_program === "company_driver"
                                  ? `${b.driver_pay_cpm}¢ CPM`
                                  : `${b.driver_pay_percentage}% ${(b.pay_program || "").replace("_", " ")}`}
                                {" · "}
                                <span style={{ color: GOLD }}>${b.placement_fee?.toLocaleString()} fee</span>
                              </p>
                              {hasCounter && b.status === "live" && (
                                <button
                                  data-testid={`carrier-revise-bid-btn-${b.id}`}
                                  onClick={() => setCarrierTab("board")}
                                  className="mt-2 px-3 py-1.5 font-head text-[10px] font-black uppercase tracking-[0.15em] transition-all hover:brightness-110"
                                  style={{ background: "rgba(245,200,66,0.12)", color: GOLD, border: "1px solid rgba(245,200,66,0.35)" }}>
                                  Revise Bid → ${b.counter_offer_fee?.toLocaleString()}
                                </button>
                              )}
                              {b.status === "payment_pending" && (
                                <button
                                  data-testid={`carrier-upload-receipt-btn-${b.id}`}
                                  onClick={() => { setPaymentNotif({ bid_id: b.id, driver_name: b.driver_name, placement_fee: b.placement_fee, payment_option: b.payment_option }); setShowPaymentModal(true); }}
                                  className="mt-2 flex items-center gap-1.5 px-3 py-1.5 font-head text-[10px] font-black uppercase tracking-[0.15em] transition-all hover:brightness-110 animate-pulse"
                                  style={{ background: "rgba(245,200,66,0.12)", color: GOLD, border: "1px solid rgba(245,200,66,0.4)" }}>
                                  Upload Payment Receipt
                                </button>
                              )}
                              {b.status === "transferred" && (
                                <button
                                  data-testid={`carrier-view-driver-btn-${b.id}`}
                                  onClick={() => setCarrierTab("mydrivers")}
                                  className="mt-2 flex items-center gap-1.5 px-3 py-1.5 font-head text-[10px] font-black uppercase tracking-[0.15em] transition-all hover:brightness-110"
                                  style={{ background: "rgba(34,197,94,0.1)", color: "#22C55E", border: "1px solid rgba(34,197,94,0.3)" }}>
                                  <Unlock className="w-3 h-3" /> View Driver Profile
                                </button>
                              )}
                              {b.status === "live" && (
                                <button
                                  data-testid={`carrier-upload-docs-btn-${b.id}`}
                                  onClick={() => {
                                    const hasCredit = docCredits?.credits?.some(c => c.app_id === b.driver_id && c.status !== "void");
                                    if (hasCredit) {
                                      toast.info("You've already earned a credit for this driver — check the Credits tab.");
                                    } else {
                                      setCarrierDocUploadBid(b);
                                    }
                                  }}
                                  className="mt-2 flex items-center gap-1.5 px-3 py-1.5 font-head text-[10px] font-black uppercase tracking-[0.15em] transition-all"
                                  style={{ background: "rgba(245,200,66,0.06)", color: "#F5C842", border: "1px solid rgba(245,200,66,0.25)" }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(245,200,66,0.12)"; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(245,200,66,0.06)"; }}>
                                  <DollarSign className="w-3 h-3" />
                                  {docCredits?.credits?.some(c => c.app_id === b.driver_id && c.status !== "void")
                                    ? "Credit Earned"
                                    : "Upload Docs — Earn $30 Credit"}
                                </button>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              <p className="font-head text-xs font-bold text-white/60">{timeAgo(b.created_at)}</p>
                              {b.payment_option === "weekly_flex" && b.driver_royalty != null && (
                                <p className="text-[10px] mt-0.5" style={{ color: GOLD }}>
                                  ${b.driver_royalty.toFixed(2)}/wk royalty
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      ) : (
        <>
          {/* ── Search + header row (Transfer Board) ── */}
          <div id="tour-driver-board" className="flex flex-col gap-3 mb-6" style={{ scrollMarginTop: 80 }}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <p className="font-head text-xs uppercase tracking-[0.4em] text-white/30 mb-1">
                  Transfer Board
                </p>
                <h2 className="font-head text-2xl font-black uppercase tracking-[0.04em]" style={{ color: ACCENT }}>
                  Available Drivers
                </h2>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
                  <input
                    data-testid="driver-board-search"
                    type="text"
                    value={boardSearch}
                    onChange={(e) => setBoardSearch(e.target.value)}
                    placeholder="Name, state, CDL-A, dry van…"
                    className="pl-8 pr-3 py-2 font-head text-xs bg-transparent text-white/80 placeholder-white/25 outline-none focus:ring-0"
                    style={{ border: "1px solid rgba(255,255,255,0.15)", width: "230px", background: "rgba(255,255,255,0.03)" }}
                  />
                  {boardSearch && (
                    <button
                      onClick={() => setBoardSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {!marketUser && (
                  <button
                    onClick={() => handleHubClick("carrier", setupComplete)}
                    className="font-head text-xs uppercase tracking-[0.2em] px-4 py-2 border border-white/15 text-white/50 hover:border-[#FF4D00] hover:text-[#FF4D00] transition-colors"
                  >
                    Login to Bid
                  </button>
                )}
              </div>
            </div>
            {/* Freight filter chips */}
            <div className="flex flex-wrap gap-2" data-testid="freight-filter-chips">
              {[
                { key: "all",       label: "All Freight" },
                { key: "dry van",   label: "Dry Van" },
                { key: "reefer",    label: "Reefer" },
                { key: "flatbed",   label: "Flatbed" },
                { key: "tanker",    label: "Tanker" },
                { key: "step deck", label: "Step Deck" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  data-testid={`freight-chip-${key.replace(" ", "-")}`}
                  onClick={() => setBoardFreight(key)}
                  className="font-head text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 transition-all"
                  style={{
                    border: `1px solid ${boardFreight === key ? "rgba(255,77,0,0.6)" : "rgba(255,255,255,0.1)"}`,
                    background: boardFreight === key ? "rgba(255,77,0,0.12)" : "transparent",
                    color: boardFreight === key ? ACCENT : "rgba(255,255,255,0.4)",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {dataLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: ACCENT }} />
            </div>
          ) : drivers.length === 0 ? (
            <div
              data-testid="market-empty-state"
              className="relative flex flex-col items-center justify-center py-24 px-8 text-center overflow-hidden"
              style={{
                background: `radial-gradient(ellipse 60% 50% at 50% 50%, rgba(255,77,0,0.08) 0%, transparent 70%), ${MID}`,
                border: `1px solid rgba(255,77,0,0.12)`,
              }}
            >
              <div className="absolute inset-0 pointer-events-none opacity-30"
                style={{ backgroundImage: `radial-gradient(circle, rgba(255,77,0,0.1) 1px, transparent 1px)`, backgroundSize: "40px 40px" }} />
              <div className="relative">
                <div className="mb-6 mx-auto flex items-center justify-center"
                  style={{ width: 80, height: 80, background: `rgba(255,77,0,0.08)`, border: `1px solid rgba(255,77,0,0.2)` }}>
                  <Truck className="w-10 h-10 opacity-60" style={{ color: ACCENT }} />
                </div>
                <p className="font-head font-black text-2xl uppercase tracking-[0.08em] text-white mb-2">
                  The Market Opens Soon
                </p>
                <p className="text-white/30 text-sm max-w-md mx-auto leading-relaxed">
                  Drivers appear here when they apply and consent to Market participation.
                  Be the first carrier in position when profiles go live.
                </p>
                {!marketUser && (
                  <button
                    onClick={() => handleHubClick("carrier", setupComplete)}
                    className="mt-8 font-head text-xs font-black uppercase tracking-[0.3em] px-6 py-3 transition-all hover:brightness-110"
                    style={{ background: ACCENT, color: "white" }}
                  >
                    Enter Carrier Hub →
                  </button>
                )}
              </div>
            </div>
          ) : (() => {
            const q = boardSearch.toLowerCase().trim();
            const filtered = (q || boardFreight !== "all")
              ? drivers.filter(d => {
                  const name    = `${d.first_name} ${d.last_name}`.toLowerCase();
                  const state   = (d.state || "").toLowerCase();
                  const cdl     = (d.cdl_class || "").toLowerCase();
                  const freight = (d.trailer_preference || d.equipment_package || "").toLowerCase();
                  const pos     = (d.position || "").toLowerCase();
                  const matchesSearch  = !q || (name.includes(q) || state.includes(q) || cdl.includes(q) || freight.includes(q) || pos.includes(q));
                  const matchesFreight = boardFreight === "all" || freight.includes(boardFreight.toLowerCase());
                  return matchesSearch && matchesFreight;
                })
              : drivers;
            const totalPages = Math.ceil(filtered.length / BOARD_PAGE_SIZE);
            const paged = filtered.slice((boardPage - 1) * BOARD_PAGE_SIZE, boardPage * BOARD_PAGE_SIZE);
            return (
              <>
                {filtered.length === 0 ? (
                  <div className="py-16 text-center" style={{ border: "1px solid rgba(255,255,255,0.06)" }}>
                    <p className="font-head text-sm text-white/30 uppercase tracking-[0.2em]">No drivers match "{boardSearch}"</p>
                    <button onClick={() => setBoardSearch("")} className="mt-3 font-head text-xs text-white/40 hover:text-white/70 underline transition-colors">Clear search</button>
                  </div>
                ) : (
                  <>
                    <motion.div
                      variants={STAGGER_CONTAINER}
                      initial="hidden"
                      animate="show"
                      className="flex flex-wrap gap-5 market-driver-grid"
                    >
                      {paged.map((d) => (
                        <DriverCard
                          key={d.id}
                          driver={d}
                          marketUser={marketUser}
                          onBid={openBidModal}
                          onViewProfile={setSelectedDriver}
                          apiBase={apiBase}
                          token={token}
                        />
                      ))}
                    </motion.div>
                    {totalPages > 1 && (
                      <div data-testid="driver-board-pagination" className="flex items-center justify-between mt-6 pt-5" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
                        <p className="font-head text-[10px] uppercase tracking-[0.2em] text-white/30">
                          {filtered.length} driver{filtered.length !== 1 ? "s" : ""} · Page {boardPage} of {totalPages}
                        </p>
                        <div className="flex items-center gap-1.5">
                          <button
                            data-testid="driver-board-prev"
                            onClick={() => setBoardPage(p => Math.max(1, p - 1))}
                            disabled={boardPage === 1}
                            className="px-3 py-1.5 font-head text-[10px] uppercase tracking-[0.12em] transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:border-[#FF4D00] hover:text-[#FF4D00]"
                            style={{ border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.5)" }}
                          >
                            <ChevronLeft className="w-3.5 h-3.5" />
                          </button>
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                            <button
                              key={p}
                              data-testid={`driver-board-page-${p}`}
                              onClick={() => setBoardPage(p)}
                              className="px-3 py-1.5 font-head text-[10px] transition-all"
                              style={{
                                border: `1px solid ${p === boardPage ? "rgba(255,77,0,0.5)" : "rgba(255,255,255,0.1)"}`,
                                background: p === boardPage ? "rgba(255,77,0,0.12)" : "transparent",
                                color: p === boardPage ? ACCENT : "rgba(255,255,255,0.4)",
                                minWidth: "32px",
                              }}
                            >
                              {p}
                            </button>
                          ))}
                          <button
                            data-testid="driver-board-next"
                            onClick={() => setBoardPage(p => Math.min(totalPages, p + 1))}
                            disabled={boardPage === totalPages}
                            className="px-3 py-1.5 font-head text-[10px] uppercase tracking-[0.12em] transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:border-[#FF4D00] hover:text-[#FF4D00]"
                            style={{ border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.5)" }}
                          >
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            );
          })()}
        </>
      )}
    </section>
  );
}
